import React from 'react';
import App from 'antd/lib/app';
import Button from 'antd/lib/button';
import Form from 'antd/lib/form';
import Input from 'antd/lib/input';
import Modal from 'antd/lib/modal';
import Switch from 'antd/lib/switch';
import Typography from 'antd/lib/typography';
import { IEmailUtilityAutomation, IUpdateEmailUtilityAutomation } from '../../domain/email-utility-automation/interface';
import { useEmailUtilityAutomationUpdateMutation } from '../../domain/email-utility-automation/queries';

interface IUtilityAutomationFormModalProps {
    utilityAutomation: IEmailUtilityAutomation;
    onClose: (needRefresh?: boolean) => void;
}

export const EmailUtilityAutomationFormModal: React.FC<IUtilityAutomationFormModalProps> = ({ utilityAutomation, onClose }) => {
    const { notification } = App.useApp();
    const [form] = Form.useForm<IEmailUtilityAutomationFormData>();

    const { mutateAsync: updateEmailUtilityAutomation, isLoading } = useEmailUtilityAutomationUpdateMutation();

    async function handleFinish(utilityAutomationData: IEmailUtilityAutomationFormData) {
        try {
            const update = EmailUtilityAutomationFormData.toEntity(utilityAutomationData);

            await updateEmailUtilityAutomation(update);

            onClose(true);
        } catch (error: any) {
            notification.error({ key: 'utility-automation-error', message: error?.message || 'Cannot save utility automation!' });
        }
    }

    const handleClose = () => {
        onClose();
    };

    return (
        <Modal
            open
            title="Edit Utility Automation"
            destroyOnHidden
            onCancel={handleClose}
            footer={[
                <Button key="cancel" onClick={handleClose}>
                    Cancel
                </Button>,
                <Button key="save" type="primary" loading={isLoading} onClick={form.submit}>
                    Save
                </Button>,
            ]}
        >
            <Form
                form={form}
                preserve={false}
                name="utility-automation-form"
                layout="vertical"
                onFinish={handleFinish}
                initialValues={EmailUtilityAutomationFormData.fromEntity(utilityAutomation)}
            >
                <Form.Item name="automation_key" hidden>
                    <Input />
                </Form.Item>

                <Form.Item name="title" label={<Typography.Text strong>Utility Automation Title</Typography.Text>}>
                    <Input disabled placeholder="Title" size="large" />
                </Form.Item>

                <Form.Item
                    name="receiving_from"
                    label={<Typography.Text strong>Receiving From</Typography.Text>}
                    tooltip="Enter comma-separated email addresses"
                    rules={[
                        { required: true, message: 'At least one email address is required' },
                        {
                            validator: (_, value: string) => {
                                if (!value) return Promise.reject('Please enter at least one email address');

                                const emails = value.split(',').map((email: string) => email.trim());
                                if (emails.length === 0 || (emails.length === 1 && !emails[0])) {
                                    return Promise.reject('Please enter at least one email address');
                                }

                                const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
                                const invalidEmails = emails.filter((email: string) => !emailRegex.test(email));

                                if (invalidEmails.length > 0) {
                                    return Promise.reject(`Invalid email format: ${invalidEmails.join(', ')}`);
                                }

                                return Promise.resolve();
                            },
                        },
                    ]}
                >
                    <Input placeholder="Enter email addresses separated by commas" size="large" />
                </Form.Item>

                <Form.Item name="parser_name" label={<Typography.Text strong>Parser Name</Typography.Text>}>
                    <Input disabled placeholder="Parser Name" size="large" />
                </Form.Item>

                <Form.Item name="is_active" label={<Typography.Text strong>Active</Typography.Text>}>
                    <Switch />
                </Form.Item>
            </Form>
        </Modal>
    );
};

export interface IEmailUtilityAutomationFormData {
    automation_key: string;
    title: string;
    is_active: boolean;
    receiving_from: string;
    parser_name: string;
}

export class EmailUtilityAutomationFormData {
    public static fromEntity(entity: IEmailUtilityAutomation): IEmailUtilityAutomationFormData {
        return {
            automation_key: entity.automation_key,
            title: entity.title,
            is_active: entity.is_active,
            parser_name: entity.parser_name,
            receiving_from: entity.receiving_from.join(', '),
        };
    }

    public static toEntity(formData: IEmailUtilityAutomationFormData): IUpdateEmailUtilityAutomation {
        return {
            automation_key: formData.automation_key,
            is_active: formData.is_active,
            receiving_from: formData.receiving_from.split(',').map(email => email.trim()),
        };
    }
}
